import { type FC, useState } from 'react'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { type EmailTemplate, EmailTemplatePurpose, SchedulingEmailAudience } from '../../../../models/Scheduling'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { type SchedulingEmailDefinition, schedulingEmailsFor } from '../../../Scheduling/shared/schedulingEmailCatalog'
import { useSchedulingEmailTemplates } from '../../../Scheduling/shared/useSchedulingEmailTemplates'
import { SettingsSection } from './SettingsSection'
import { TemplateCard, TemplateGroup, TemplateText } from './messageTemplateCards'
import { CHIP_CLASS, transformTemplateBodyHtml } from './messageTemplatePlaceholders'
import { SmsTemplateGroup } from './SmsTemplateGroup'

interface EmailTemplatePreviewProps {
  template?: EmailTemplate
}

const EmailTemplatePreview: FC<EmailTemplatePreviewProps> = ({ template }) =>
  template != null ? (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex flex-column zocks-gap-0.5 p-3 rounded-2 zocks-bg-color-gray-100 paragraph-small'>
        <div className='d-flex align-items-baseline gap-2'>
          <span className='lead-small zocks-text-gray-500 text-uppercase'>From</span>
          <span>
            <span className={CHIP_CLASS}>Advisor full name</span> via Zocks · notifications@booking.zocks.com
          </span>
        </div>
        <div className='d-flex align-items-baseline gap-2'>
          <span className='lead-small zocks-text-gray-500 text-uppercase'>Subject</span>
          <span>
            <TemplateText text={template.subject} />
          </span>
        </div>
      </div>
      <div className='pe-none' dangerouslySetInnerHTML={{ __html: transformTemplateBodyHtml(template.body, CHIP_CLASS) }} />
    </div>
  ) : (
    <div className='paragraph-small zocks-text-gray-600'>This template isn&apos;t available yet.</div>
  )

interface EmailTemplateGroupProps {
  title: string
  emails: SchedulingEmailDefinition[]
  byPurpose: (purpose: EmailTemplatePurpose) => EmailTemplate | undefined
  expandedId: string | null
  onToggle: (id: string) => void
}

const EmailTemplateGroup: FC<EmailTemplateGroupProps> = ({ title, emails, byPurpose, expandedId, onToggle }) => (
  <TemplateGroup title={title}>
    {emails.map((email) => {
      const id = `email-template-${email.purpose}`
      return (
        <TemplateCard key={id} regionId={id} label={email.label} description={email.description} isExpanded={expandedId === id} onToggle={() => onToggle(id)}>
          <EmailTemplatePreview template={byPurpose(email.purpose)} />
        </TemplateCard>
      )
    })}
  </TemplateGroup>
)

export const LegacyMessageTemplatesSection: FC = () => {
  const { isLoading, isError, byPurpose } = useSchedulingEmailTemplates()
  const { hasQuota } = useZocksQuota()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const showSms = hasQuota('scheduling.smsNotifications.value')
  const emailTemplatesReady = !isLoading && !isError

  const clientEmails = schedulingEmailsFor(SchedulingEmailAudience.CLIENTS).filter(
    (email) => hasQuota('scheduling.reminders.value') || email.purpose !== EmailTemplatePurpose.MEETING_REMINDER,
  )

  const handleToggle = (id: string) => {
    setExpandedId((current) => (current === id ? null : id))
  }

  return (
    <SettingsSection
      title={showSms ? 'Message templates' : 'Email templates'}
      titleAdornment={
        <InfoTooltip
          title="These templates are managed for you at the moment. Let support know what you'd change and help us improve them."
          label={showSms ? 'About message templates' : 'About email templates'}
        />
      }
      subtitle={
        showSms
          ? 'The emails and texts Zocks sends for you, with details filled in automatically. Booking and reschedule confirmations come from your calendar, not from here.'
          : 'The scheduling emails Zocks sends for you, with details filled in automatically. Booking and reschedule confirmations come from your calendar, not from here.'
      }
    >
      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600'>Could not load the email templates. Please try again.</div> : null}
      {emailTemplatesReady ? (
        <EmailTemplateGroup
          title={showSms ? 'Emails sent to clients on your behalf' : 'Sent to clients on your behalf'}
          emails={clientEmails}
          byPurpose={byPurpose}
          expandedId={expandedId}
          onToggle={handleToggle}
        />
      ) : null}
      {showSms ? <SmsTemplateGroup expandedId={expandedId} onToggle={handleToggle} /> : null}
      {emailTemplatesReady ? (
        <EmailTemplateGroup
          title={showSms ? 'Emails sent to team members' : SchedulingEmailAudience.ADVISOR}
          emails={schedulingEmailsFor(SchedulingEmailAudience.ADVISOR)}
          byPurpose={byPurpose}
          expandedId={expandedId}
          onToggle={handleToggle}
        />
      ) : null}
    </SettingsSection>
  )
}
